import { type QueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { CatchBoundary, createRoute, Outlet, redirect, useRouter, useRouterState, type AnyRoute } from "@tanstack/react-router";
import { Suspense, useCallback } from "react";

import NotFound from "@/app/not-found";
import RouteError from "@/app/route-error";
import { RpcError } from "@buildpy/api/client";
import { dropRejectedSession, hasSession, isNoMemberError } from "@/api/client";
import { LOGIN_PATH, loadView, publicViewQuery, viewQuery } from "@/api/views";
import { loginHref, loginNextOf } from "@buildpy/core/modules/onboarding/rules";
import { brandHead, customerHead, NOINDEX_ROBOTS, withRobots } from "@/lib/head";
import { reportError } from "@/lib/monitoring";
import { PublicEnvError } from "@/lib/public-env";
import { readAccessToken } from "@/lib/supabase";
import { signupStatus } from "@/api/actions/onboarding";
import { FORGOT_PASSWORD_PATH, ONBOARDING_PATH, SIGNUP_VERIFY_PATH } from "@/modules/plan/copy";
import ForgotPasswordPage from "@/pages/(auth)/forgot-password/page";
import InvitePage from "@/pages/(auth)/invite/[token]/page";
import LoginPage from "@/pages/(auth)/login/page";
import ResetPasswordPage from "@/pages/(auth)/reset-password/page";
import { clearResetSearch, type ResetNotice, resetNoticeOf, useHeldResetNotice } from "@/pages/(auth)/reset-password/reset-form-state";
import SignupPage from "@/pages/(auth)/signup/page";
import SignupVerifyPage from "@/pages/(auth)/signup/verify/page";
import AppLayout from "@/pages/app/layout";
import Loading from "@/pages/app/loading";
import CustomerLayoutC from "@/pages/c/layout";
import OnboardingPage from "@/pages/onboarding/page";
import DemoLayout from "@/pages/demo/layout";
import CustomerLayoutS from "@/pages/s/layout";
import ToolsLayout from "@/pages/tools/layout";
import { rootRoute } from "./root-route";

// /app 아래 화면이 펼쳐 쓰는 경계 — 셸을 남긴 채 본문 자리에서 404·오류·로딩을 그린다.
export const APP_BOUNDARIES = { errorComponent: RouteError, notFoundComponent: NotFound, pendingComponent: Loading } as const;

const BOUNDARIES = { errorComponent: RouteError, notFoundComponent: NotFound } as const;

const AppRouteComponent = () => {
  const { data } = useSuspenseQuery(viewQuery("shell"));
  const resetKey = useRouterState({ select: (state) => state.location.href });
  return (
    <AppLayout shell={data}>
      <CatchBoundary errorComponent={RouteError} getResetKey={() => resetKey} onCatch={(error) => reportError(error)}>
        <Suspense fallback={<Loading />}>
          <Outlet />
        </Suspense>
      </CatchBoundary>
    </AppLayout>
  );
};

export const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
  // 세션이 없으면 셸·하위 화면 읽기(loader)를 보내기 전에 로그인으로(?next= 원래 주소) — 세션은 있는데 401 이면 loadView 가 (토큰 무효일 때만 지우고) 보낸다.
  beforeLoad: async ({ location }) => {
    if (!(await hasSession())) throw redirect({ href: loginHref(location.href) });
  },
  loader: ({ context, location }) => loadView(context.queryClient, viewQuery("shell"), location.href),
  component: AppRouteComponent,
  ...BOUNDARIES,
});

export const cRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/c",
  head: () => customerHead(),
  component: () => (
    <CustomerLayoutC>
      <Outlet />
    </CustomerLayoutC>
  ),
  ...BOUNDARIES,
});

export const sRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/s",
  head: () => customerHead(),
  component: () => (
    <CustomerLayoutS>
      <Outlet />
    </CustomerLayoutS>
  ),
  ...BOUNDARIES,
});

export const demoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/demo",
  component: () => (
    <DemoLayout>
      <Outlet />
    </DemoLayout>
  ),
  ...BOUNDARIES,
});

export const toolsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tools",
  component: () => (
    <ToolsLayout>
      <Outlet />
    </ToolsLayout>
  ),
  ...BOUNDARIES,
});

// 가입 상태가 「멤버 아님」 으로 끝나는 응답 — 401(signupStatus 는 user 액션이라 no_member 없이 토큰 무효뿐) · 403 메일 확인 전(멤버일 수 없다) · 503 은 「모름」 이라 여기 없다(오류 화면).
const NOT_MEMBER_STATUSES = new Set([401, 403]);

type MemberState = "member" | "no_member" | "none";

// 인증 서버 장애(503)는 멤버 여부를 모른다 — 세션을 남긴 채 /onboarding·/app 과 같은 오류 화면으로(로그인 폼을 보이면 로그아웃된 줄 안다) · 공개 env 가 없어도 오류 화면.
const rethrowUnknown = (error: unknown): void => {
  if (error instanceof PublicEnvError || (error instanceof RpcError && error.status === 503)) throw error;
};

// 이 브라우저 세션의 가입 상태 — 세션이 없으면 묻지 않고 none · 가입 상태(signupStatus.member)로 먼저 가르고 · 그 판정을 못 받으면 셸로 다시 본다.
const memberStateOf = async (queryClient: QueryClient): Promise<MemberState> => {
  if (!(await hasSession())) return "none";
  const status = await signupStatus().catch(async (error: unknown) => {
    rethrowUnknown(error);
    if (!(error instanceof RpcError) || !NOT_MEMBER_STATUSES.has(error.status)) return undefined;
    if (error.status === 401) await dropRejectedSession(error);
    return null;
  });
  if (status === null) return "none";
  if (status !== undefined && !status.member) return "no_member";
  return queryClient.fetchQuery(viewQuery("shell")).then(
    (): MemberState => "member",
    async (error: unknown): Promise<MemberState> => {
      rethrowUnknown(error);
      if (isNoMemberError(error)) return "no_member";
      if (error instanceof RpcError && error.status === 401) await dropRejectedSession(error);
      return "none";
    },
  );
};

// 로그인·가입·비밀번호 찾기 입구 한 벌 — 멤버면 to(기본 /app) · 계정만 있고 멤버가 없으면 /onboarding(로그인 액션의 needsOnboarding 과 같은 곳) · 아니면 폼.
const leaveSignedIn = async (queryClient: QueryClient, to = "/app"): Promise<void> => {
  const state = await memberStateOf(queryClient);
  if (state === "member") throw redirect({ href: to });
  if (state === "no_member") throw redirect({ href: ONBOARDING_PATH });
};

// ?reset= 은 화면 상태로 옮기고 주소에선 지운다(replace — 뒤로가기 기록도 안 남긴다).
const LoginRouteComponent = () => {
  const router = useRouter();
  const clearReset = useCallback(() => clearResetSearch(router), [router]);
  const reset = useHeldResetNotice(loginRoute.useSearch().reset, clearReset);
  const next = loginRoute.useSearch().next;
  return <LoginPage next={next} reset={reset} />;
};

// 멤버면 /app(또는 ?next=) · 계정만 있고 멤버가 없으면 /onboarding · ?reset= 은 비밀번호를 바꾸고 온 안내.
export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>): { next?: string; reset?: ResetNotice } => ({
    next: loginNextOf(search.next) ?? undefined,
    reset: resetNoticeOf(search.reset),
  }),
  // 토큰이 무효인 401 세션만 지운다(dev 메모리 인증은 api 재시작마다 빈다) · 멤버만 없는 세션(온보딩 중)은 남긴다.
  beforeLoad: ({ context, search }) => leaveSignedIn(context.queryClient, search.next),
  component: LoginRouteComponent,
  ...BOUNDARIES,
});

// 셀프 가입 — 이미 멤버면 /app · 멤버 없는 계정이면 /onboarding(로그인과 같은 규칙).
export const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  beforeLoad: ({ context }) => leaveSignedIn(context.queryClient),
  head: () => withRobots(brandHead("무료로 시작"), NOINDEX_ROBOTS),
  component: SignupPage,
  ...BOUNDARIES,
});

// 비밀번호 찾기 — 이미 멤버면 /app · 멤버 없는 계정이면 /onboarding(로그인과 같은 규칙) · 메일 링크는 /reset-password 로 닿는다.
export const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: FORGOT_PASSWORD_PATH,
  beforeLoad: ({ context }) => leaveSignedIn(context.queryClient),
  head: () => withRobots(brandHead("비밀번호 찾기"), NOINDEX_ROBOTS),
  component: ForgotPasswordPage,
  ...BOUNDARIES,
});

// 가입 확인 메일이 닿는 곳 — 링크 토큰을 읽어야 하므로 beforeLoad 가 없다(reset-password 와 같다).
export const signupVerifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup/verify",
  head: () => withRobots(brandHead("메일 확인"), NOINDEX_ROBOTS),
  component: SignupVerifyPage,
  ...BOUNDARIES,
});

// 시공사 만들기 — 이미 멤버면 /app · 세션이 없거나 못 물으면 /login · 비밀번호를 아직 안 정했으면 /signup/verify(정하기 단계).
export const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  beforeLoad: async () => {
    if (!(await readAccessToken())) throw redirect({ to: LOGIN_PATH });
    const status = await signupStatus().catch(async (error: unknown) => {
      // 인증 서버 장애(503)는 로그인으로 보내지 않고 오류 화면 — 세션·초안을 남긴다.
      if (error instanceof RpcError && error.status === 503) throw error;
      if (error instanceof RpcError && error.status === 401) await dropRejectedSession(error);
      return null;
    });
    if (!status) throw redirect({ to: LOGIN_PATH });
    if (status.member) throw redirect({ to: "/app" });
    if (status.needsPassword) throw redirect({ to: SIGNUP_VERIFY_PATH });
    return { signupUserId: status.userId };
  },
  head: () => withRobots(brandHead("시공사 만들기"), NOINDEX_ROBOTS),
  component: () => <OnboardingPage userId={onboardingRoute.useRouteContext().signupUserId} />,
  ...BOUNDARIES,
});

const InviteRouteComponent = () => {
  const { token } = inviteRoute.useParams();
  const { data } = useSuspenseQuery(publicViewQuery("invite", [token]));
  return <InvitePage data={data} />;
};

export const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite/$token",
  loader: ({ context, params }) => loadView(context.queryClient, publicViewQuery("invite", [params.token])),
  component: InviteRouteComponent,
  ...BOUNDARIES,
});

// 재설정 메일이 닿는 곳 — 복구 세션이 셸을 읽으면 /app 으로 가버리므로 login 과 달리 beforeLoad 가 없다.
export const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
  ...BOUNDARIES,
});

// w0b 배치의 라우트 — 다른 배치는 위 레이아웃 route 를 getParentRoute 로 가리킨다.
export const routes: AnyRoute[] = [
  appRoute,
  cRoute,
  sRoute,
  demoRoute,
  toolsRoute,
  loginRoute,
  forgotPasswordRoute,
  signupRoute,
  signupVerifyRoute,
  onboardingRoute,
  resetPasswordRoute,
  inviteRoute,
];
